import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router';

import { Button } from '@actual-app/components/button';
import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { TextOneLine } from '@actual-app/components/text-one-line';
import { Toggle } from '@actual-app/components/toggle';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';
import { currencyToInteger } from '@actual-app/core/shared/util';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import type { CategoryEntity } from '@actual-app/core/types/models';
import type { Template } from '@actual-app/core/types/models/templates';

import {
  useBudgetActions,
  useDeleteCategoryMutation,
  useSaveCategoryMutation,
} from '#budget';
import type { ApplyBudgetActionPayload } from '#budget';
import { AyudaGavi } from '#components/mobile/ayuda/AyudaGavi';
import { OjoBuey } from '#components/mobile/ayuda/OjoBuey';
import { usePreguntasFicha } from '#components/mobile/ayuda/usePreguntasFicha';
import { CheckPagada } from '#components/mobile/budget/CheckPagada';
import {
  formularioDesde,
  objetivoDesde,
  Pestanas,
  useGuardarObjetivo,
  useResumenObjetivo,
} from '#components/mobile/budget/ObjetivoPage';
import type { Formulario } from '#components/mobile/budget/ObjetivoPage';
import {
  CADENCIAS_ANUALES,
  cuotaMensual,
  estadoFila,
  faltante,
  lineasDeObjetivo,
  metaDeObjetivo,
  notaSinObjetivo,
  objetivoDesdePlantillas,
  opcionSuficiente,
  separarObjetivoDeNota,
  unirNotaYObjetivo,
} from '#components/mobile/budget/objetivos';
import type {
  CadenciaMeses,
  Objetivo,
} from '#components/mobile/budget/objetivos';
import { esPagada, marcaManual } from '#components/mobile/budget/pagadas';
import { esObjetivoConFecha } from '#components/mobile/budget/recuerdos';
import { BotellaFicha } from '#components/mobile/budget/travesia/BotellaFicha';
import { lineasConFecha } from '#components/mobile/budget/travesias';
import { useRefrescarObjetivos } from '#components/mobile/budget/useDatosObjetivos';
import {
  useDespertarAuto,
  useIgnorarMes,
  usePausarMes,
} from '#components/mobile/budget/useIgnorarMes';
import { usePagadas } from '#components/mobile/budget/usePagadas';
import { useTravesiaCategoria } from '#components/mobile/budget/useTravesiaCategoria';
import type { TravesiaCategoria } from '#components/mobile/budget/useTravesiaCategoria';
import { evolucionCategoria } from '#components/mobile/informes/calculos';
import { useTotalesMensuales } from '#components/mobile/informes/useTotalesMensuales';
import { MobileBackButton } from '#components/mobile/MobileBackButton';
import { InputField } from '#components/mobile/MobileForms';
import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import {
  opcionAnadirMovimiento,
  useOpcionesPantalla,
} from '#components/mobile/opciones/OpcionesPantalla';
import { GaviFicha } from '#components/mobile/pagosGavi/GaviFicha';
import { RecibosGavi } from '#components/mobile/pagosGavi/RecibosGavi';
import { usePagosGavi } from '#components/mobile/pagosGavi/usePagosGavi';
import { BarraProgreso } from '#components/mobile/ui/BarraProgreso';
import { Boton } from '#components/mobile/ui/Boton';
import { CampoImporte } from '#components/mobile/ui/CampoImporte';
import { CampoNombreEmoji } from '#components/mobile/ui/CampoNombreEmoji';
import { ConfirmarHoja } from '#components/mobile/ui/ConfirmarHoja';
import { separarEmoji, unirEmoji } from '#components/mobile/ui/emoji';
import { useEstadoDeVista } from '#components/mobile/ui/estadoDeVista';
import { Hoja } from '#components/mobile/ui/Hoja';
import { Icono } from '#components/mobile/ui/Icono';
import type { NombreIcono } from '#components/mobile/ui/Icono';
import { IconoCaja } from '#components/mobile/ui/IconoCaja';
import { Pildora } from '#components/mobile/ui/Pildora';
import type { EstadoPildora } from '#components/mobile/ui/Pildora';
import { estiloTarjeta } from '#components/mobile/ui/Tarjeta';
import {
  color,
  densidad,
  espacio,
  num,
  radio,
  TACTIL,
  texto,
} from '#components/mobile/ui/tokens';
import { Notes } from '#components/Notes';
import { MobilePageHeader, Page } from '#components/Page';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useCategory } from '#hooks/useCategory';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';
import { useNotes } from '#hooks/useNotes';
import { usePinnedCategories } from '#hooks/usePinnedCategories';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useSheetValue } from '#hooks/useSheetValue';
import { useUndo } from '#hooks/useUndo';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';
import { envelopeBudget } from '#spreadsheet/bindings';

import { AnimacionSuficiente, MarcaSuficiente } from './AnimacionSuficiente';
import { AsesorFicha } from './AsesorFicha';
import { CuentaFicha } from './CuentaFicha';
import { DetallePagoFijo } from './DetallePagoFijo';
import {
  ChipsEvolucion,
  GraficoGasto,
  GraficoSaldo,
  MapaMeses,
} from './EvolucionFicha';
import type { VistaEvolucion } from './EvolucionFicha';
import {
  bandaFicha,
  colorProgreso,
  esObjetivoDeSaldo,
  filasEstacionalidad,
  historiaGasto,
  mesesAnteriores,
  mesesConHistoria,
  opcionesSueno,
  resumenObjetivo,
  sugerenciasImporte,
} from './fichaCalculos';
import type { EstadoObjetivo } from './fichaCalculos';
import { MovimientosMes } from './MovimientosMes';
import { useHistoriaCategoria } from './useDatosFicha';

/** Meses de historial (3 años + el actual): misma caché que el análisis. */
const MESES_HISTORIAL = 37;

/**
 * Ficha única de una categoría («Diseño 1 · Ficha única»): disponible del
 * mes con su desglose, objetivo editable en el sitio (con «Ignorar este
 * mes»), evolución (gasto, saldo, mapa de meses), lo que opina el Asesor,
 * notas y acciones. Sustituye al doble paso «Detalles» → «Objetivo».
 */
type FichaCategoriaPageProps = {
  /**
   * La enseña la ayuda de Gavi sobre otra pantalla (Ahorros): esta
   * categoría, sin «atrás» ni ojo de buey propios.
   */
  vistaPrevia?: { categoryId: string };
};

export function FichaCategoriaPage({ vistaPrevia }: FichaCategoriaPageProps) {
  const { id: idRuta = '' } = useParams();
  const categoryId = vistaPrevia?.categoryId ?? idRuta;
  const [searchParams] = useSearchParams();
  const monthParam = searchParams.get('month');
  const month =
    monthParam && monthUtils.isValidYearMonth(monthParam)
      ? monthParam
      : monthUtils.currentMonth();
  const editar = searchParams.get('editar') === '1';
  const { data: category } = useCategory(categoryId);
  const [ayudaAbierta, setAyudaAbierta] = useState(false);

  return (
    <Page
      padding={0}
      header={
        <MobilePageHeader
          title={
            <span data-ayuda="titulo-ficha">
              <TextOneLine>{category?.name ?? ''}</TextOneLine>
            </span>
          }
          leftContent={vistaPrevia ? undefined : <MobileBackButton />}
          rightContent={
            category &&
            !vistaPrevia && <OjoBuey onPress={() => setAyudaAbierta(true)} />
          }
        />
      }
    >
      {category && (
        <SheetNameProvider name={monthUtils.sheetForMonth(month)}>
          <ContenidoFicha
            key={category.id}
            category={category}
            month={month}
            editarAlAbrir={editar}
            ayudaAbierta={ayudaAbierta}
            onCerrarAyuda={() => setAyudaAbierta(false)}
          />
        </SheetNameProvider>
      )}
    </Page>
  );
}

/**
 * Brújula de la ficha (antes, su menú «⋯»): fijar en Inicio, mover dinero
 * desde esta categoría (o «Cubrir» si está en rojo) y movimientos.
 */
function useOnBudgetAction() {
  const applyBudgetAction = useBudgetActions();
  return useCallback(
    (mes: string, type: string, args: unknown) => {
      applyBudgetAction.mutate({
        month: mes,
        type,
        args,
      } as ApplyBudgetActionPayload);
    },
    [applyBudgetAction],
  );
}

/**
 * B4 · H44 / H49: asigna en `month` lo que falta (suma a lo ya asignado),
 * con aviso y «Deshacer».
 */
function useAsignarFalta(category: CategoryEntity, month: string) {
  const { t } = useTranslation();
  const locale = useLocale();
  const format = useFormat();
  const applyBudgetAction = useBudgetActions();
  const { showUndoNotification } = useUndo();
  return useCallback(
    (importe: IntegerAmount, asignadoAntes: IntegerAmount) => {
      if (importe <= 0) {
        return;
      }
      applyBudgetAction.mutate({
        month,
        type: 'budget-amount',
        args: { category: category.id, amount: asignadoAntes + importe },
      } as ApplyBudgetActionPayload);
      showUndoNotification({
        message: t('{{amount}} assigned to {{categoryName}} in {{month}}.', {
          amount: format(importe, 'financial'),
          categoryName: category.name,
          month: monthUtils.format(month, 'MMMM', locale),
        }),
      });
    },
    [
      applyBudgetAction,
      category,
      format,
      locale,
      month,
      showUndoNotification,
      t,
    ],
  );
}

/** Abre «Mover» con la categoría como destino (traer dinero a ella). */
function useMoverHacia(categoryId: string, month: string) {
  const dispatch = useDispatch();
  const onBudgetAction = useOnBudgetAction();
  return useCallback(
    () =>
      dispatch(
        pushModal({
          modal: {
            name: 'mover',
            options: {
              month,
              onBudgetAction,
              origen: categoryId,
              modoInicial: 'hacia',
            },
          },
        }),
      ),
    [categoryId, dispatch, month, onBudgetAction],
  );
}

function OpcionesFicha({
  category,
  month,
  falta,
  onAsignar,
  travesia,
}: {
  category: CategoryEntity;
  month: string;
  /** Si es una travesía: «Carta de la travesía» y «Cerrar la travesía». */
  travesia: TravesiaCategoria | null;
  /** Lo que falta este mes (0 si nada): muestra «Asignar X». */
  falta: IntegerAmount;
  onAsignar: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const format = useFormat();
  const { isPinned, togglePinned } = usePinnedCategories();
  const fijada = isPinned(category.id);
  const onBudgetAction = useOnBudgetAction();
  const saldo =
    useSheetValue<'envelope-budget', 'leftover'>(
      envelopeBudget.catBalance(category.id),
    ) ?? 0;
  const enRojo = saldo < 0;
  const esTravesia = travesia?.tipo === 'travesia' && !category.is_income;
  const abrirHoja = (name: 'zarpar' | 'cerrar-travesia') =>
    dispatch(
      pushModal({
        modal: { name, options: { categoryId: category.id, month } },
      }),
    );
  useOpcionesPantalla([
    {
      id: 'asignar',
      tipo: 'propia',
      texto: t('Assign {{amount}}', {
        amount: format(falta, 'financial'),
      }),
      icono: 'plus',
      onPress: onAsignar,
      oculta: falta <= 0,
    },
    {
      id: 'mover',
      tipo: 'propia',
      texto: enRojo ? t('Cover') : t('Move money'),
      icono: enRojo ? 'alert' : 'arrowupdown',
      onPress: () =>
        dispatch(
          pushModal({
            modal: enRojo
              ? {
                  name: 'cubrir',
                  options: {
                    month,
                    onBudgetAction,
                    categoriaInicial: category.id,
                  },
                }
              : {
                  name: 'mover',
                  options: { month, onBudgetAction, origen: category.id },
                },
          }),
        ),
      oculta: !!category.is_income,
    },
    {
      id: 'fijar',
      tipo: 'propia',
      texto: fijada ? t('Unpin from home') : t('Pin to home'),
      icono: 'anchor',
      onPress: () => togglePinned(category.id),
      oculta: !!category.is_income,
    },
    {
      id: 'zarpar',
      tipo: 'propia',
      texto: travesia?.recuerdo ? t('Voyage letter') : t('Set sail'),
      icono: 'velero',
      onPress: () => abrirHoja('zarpar'),
      oculta: !esTravesia,
    },
    {
      id: 'cerrar-travesia',
      tipo: 'propia',
      texto: t('Close the voyage'),
      icono: 'compass',
      onPress: () => abrirHoja('cerrar-travesia'),
      oculta: !esTravesia,
    },
    // Sin «Ver movimientos» (H59): ya está a la vista en la ficha («Ver
    // movimientos de octubre») y «Actividad» abre la lista completa.
    // La general de la brújula, con la categoría ya puesta.
    opcionAnadirMovimiento(
      t('Add transaction'),
      () =>
        void navigate('/transactions/new', {
          state: { categoryId: category.id },
        }),
    ),
  ]);
  return null;
}

function plantillasDe(goalDef: string | null | undefined): Template[] {
  if (!goalDef) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(goalDef);
    return Array.isArray(parsed) ? (parsed as Template[]) : [];
  } catch {
    return [];
  }
}

function ContenidoFicha({
  category,
  month,
  editarAlAbrir,
  ayudaAbierta,
  onCerrarAyuda,
}: {
  category: CategoryEntity;
  month: string;
  editarAlAbrir: boolean;
  ayudaAbierta: boolean;
  onCerrarAyuda: () => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const navigate = useNavigate();
  const id = category.id;
  const nota = useNotes(id);

  const asignadoHoja = useSheetValue<'envelope-budget', 'budget'>(
    envelopeBudget.catBudgeted(id),
  );
  const actividadHoja = useSheetValue<'envelope-budget', 'sum-amount'>(
    envelopeBudget.catSumAmount(id),
  );
  const saldoHoja = useSheetValue<'envelope-budget', 'leftover'>(
    envelopeBudget.catBalance(id),
  );
  const asignado = asignadoHoja ?? 0;
  const actividad = actividadHoja ?? 0;
  const saldo = saldoHoja ?? 0;
  const goal = useSheetValue<'envelope-budget', 'goal'>(
    envelopeBudget.catGoal(id),
  );
  const longGoal =
    useSheetValue<'envelope-budget', 'long-goal'>(
      envelopeBudget.catLongGoal(id),
    ) === 1;
  // La cuota del mes = `goal-<id>` de Actual, recalculado al abrir.
  useRefrescarObjetivos(month);
  const sueno = useIgnorarMes(id, month);
  // «Pagada este mes» (`pagadas.ts`): pago fijo cargado o marca manual.
  const pagos = usePagadas(month);
  const pagada = esPagada({
    fijo: pagos.fijos.has(id),
    manual: pagos.manuales.includes(id),
    noPagada: marcaManual(pagos.manuales, id) === 'no-pagada',
    datos: {
      goal: goal ?? null,
      longGoal: false,
      budgeted: asignado,
      balance: saldo,
      spent: actividad,
    },
  });
  const { ignorada } = sueno;
  // Despertar automático al verla (fecha, gasto o dinero que sale): pop.
  const [pop, setPop] = useState(0);
  useDespertarAuto({
    categoryId: id,
    month,
    actividad: actividadHoja,
    saldo: saldoHoja,
    asignado: asignadoHoja,
    onDespierta: () => setPop(Date.now()),
  });

  const objetivo = useMemo(
    () => objetivoDesdePlantillas(plantillasDe(category.goal_def), nota),
    [category.goal_def, nota],
  );
  const objetivoValido =
    objetivo != null && objetivo !== 'otro' ? objetivo : null;
  // Meta con fecha que no es un pago fijo: en pantalla, una «Travesía».
  const travesia = useTravesiaCategoria(category);
  const objetivoConFecha =
    !category.is_income &&
    travesia.tipo === 'travesia' &&
    esObjetivoConFecha(objetivoValido)
      ? objetivoValido
      : null;
  const resumen = objetivoValido
    ? resumenObjetivo({
        objetivo: objetivoValido,
        saldo,
        asignado,
        actividad,
        goal,
        month,
      })
    : null;

  // Historia: 36 meses completos de gasto y 12 + el actual de saldo.
  const { movimientos } = useTotalesMensuales({
    meses: MESES_HISTORIAL,
    hasta: month,
    incluirOcultas: true,
  });
  const meses36 = useMemo(() => mesesAnteriores(month, 36), [month]);
  const gasto36 = useMemo(
    () => evolucionCategoria(movimientos, id, meses36, !!category.is_income),
    [movimientos, id, meses36, category.is_income],
  );
  const meses12 = meses36.slice(-12);
  const gasto12 = gasto36.slice(-12);
  const mesesSaldo = useMemo(
    () => [...mesesAnteriores(month, 12), month],
    [month],
  );
  const historia = useHistoriaCategoria(id, mesesSaldo);
  const filasMapa = useMemo(() => filasEstacionalidad(month, 2), [month]);
  const valoresMapa = filasMapa.map(fila =>
    fila.map(mes => gasto36[meses36.indexOf(mes)] ?? 0),
  );
  const banda = bandaFicha(gasto36);

  const deSaldo = esObjetivoDeSaldo(objetivoValido, longGoal);
  // Movimientos bajo la gráfica: el mes tocado (o el visto).
  const [mesMovs, setMesMovs] = useState(month);
  const [movsAbierto, setMovsAbierto] = useState(false);
  const abrirMovs = useCallback((mes: string) => {
    setMesMovs(mes);
    setMovsAbierto(true);
  }, []);
  // Pago fijo con Gavi (Altamar): «Evolución» abre en «Recibos», un cabo por
  // comercio, hasta que la persona elige otra vista.
  const gavi = usePagosGavi(pagos.fijos);
  const recibos =
    gavi.activo && pagos.fijos.has(id) ? (gavi.porCategoria.get(id) ?? []) : [];
  const [vistaElegida, setVista] = useState<VistaEvolucion | null>(null);
  const vista: VistaEvolucion =
    vistaElegida === 'recibos' && recibos.length === 0
      ? 'gasto'
      : (vistaElegida ??
        (recibos.length > 0 ? 'recibos' : deSaldo ? 'saldo' : 'gasto'));

  const datosMes = {
    goal: goal ?? null,
    longGoal,
    budgeted: asignado,
    balance: saldo,
    spent: actividad,
    ignorada,
    meta: metaDeObjetivo(objetivoValido),
  };
  const estado = estadoFila(datosMes);
  // «Suficiente» (= snooze de YNAB): solo con el objetivo por debajo de lo
  // planeado este mes, o para quitar la marca.
  const suficiente = opcionSuficiente(datosMes);
  // H44: lo que falta en el mes visto, si aún está en curso (o es futuro).
  const faltaMes =
    estado.tipo === 'falta' && month >= monthUtils.currentMonth()
      ? estado.importe
      : 0;
  const asignarFalta = useAsignarFalta(category, month);
  const asignarLoQueFalta = () => asignarFalta(faltaMes, asignado);
  const moverHacia = useMoverHacia(id, month);
  const sugerencias = useMemo(() => sugerenciasImporte(gasto36), [gasto36]);

  const nombreMes = monthUtils.format(month, 'MMMM', locale);
  const nombreMesCorto = monthUtils
    .format(month, 'MMM', locale)
    .replace('.', '');
  const format = useFormat();
  const preguntasAyuda = usePreguntasFicha({
    nombre: category.name,
    esIngreso: !!category.is_income,
    estado: estado.tipo,
    falta:
      !category.is_income && faltaMes > 0
        ? format(faltaMes, 'financial')
        : null,
    atrasado: resumen?.estado === 'atrasado' && !objetivoConFecha,
    hayObjetivo: objetivo !== null,
    travesia: objetivoConFecha != null,
    metaEnSaldo:
      objetivoValido?.tipo === 'una-vez' || objetivoValido?.tipo === 'anual',
    pagoFijo: pagos.fijos.has(id),
    oculta: !!category.hidden,
  });

  return (
    <View
      style={{
        padding: `${espacio.fila}px ${espacio.margen}px ${MOBILE_NAV_HEIGHT + espacio.margen}px`,
        flexShrink: 0,
        gap: espacio.fila + 4,
      }}
    >
      <OpcionesFicha
        category={category}
        month={month}
        falta={category.is_income ? 0 : faltaMes}
        onAsignar={asignarLoQueFalta}
        travesia={travesia}
      />
      <Cabecera
        nombreMes={nombreMes}
        nombreMesCorto={nombreMesCorto}
        saldo={saldo}
        asignado={asignado}
        actividad={actividad}
        estado={estado}
        falta={category.is_income ? 0 : faltaMes}
        onAsignar={asignarLoQueFalta}
        onMover={moverHacia}
        colorAnillo={colorProgreso(
          // Una travesía nunca se pinta «atrasada» (solo informativo).
          objetivoConFecha && resumen?.estado === 'atrasado'
            ? null
            : (resumen?.estado ?? null),
          ignorada,
        )}
        resumen={resumen}
        progreso={resumen?.progreso ?? null}
        meta={resumen?.meta ?? null}
        onActividad={() => void navigate(`/categories/${id}?month=${month}`)}
        pagada={pagada}
      />

      {/* Travesía (meta con fecha que no es pago fijo): una sola tarjeta
          «Travesía» con la botella navegando hacia el muelle y, debajo, la
          meta editable. */}
      <TarjetaObjetivo
        category={category}
        month={month}
        objetivo={objetivo}
        estado={resumen?.estado ?? null}
        resumen={resumen}
        saldo={saldo}
        asignado={asignado}
        actividad={actividad}
        sueno={sueno}
        ofreceSuficiente={suficiente}
        pop={pop}
        editarAlAbrir={editarAlAbrir}
        sugerencias={category.is_income ? null : sugerencias}
        onAsignar={asignarFalta}
        travesia={travesia}
        envoltorio={
          objetivoConFecha
            ? ({ derecha, cuerpo }) => (
                <BotellaFicha
                  categoriaId={id}
                  nombre={category.name}
                  objetivo={objetivoConFecha}
                  saldo={saldo}
                  asignado={asignado}
                  cuota={resumen?.cuota ?? null}
                  asignadosPrevios={historia.asignado.slice(0, 12)}
                  month={month}
                  derecha={derecha}
                  pie={cuerpo}
                />
              )
            : undefined
        }
      />

      <Seccion
        icono="pie"
        titulo={t('Evolution')}
        testId="ficha-evolucion"
        derecha={
          <ChipsEvolucion
            vista={vista}
            onChange={setVista}
            conRecibos={recibos.length > 0}
          />
        }
      >
        {vista === 'recibos' && <RecibosGavi series={recibos} />}
        {vista === 'gasto' && (
          <GraficoGasto
            meses={meses12}
            gasto={gasto12}
            asignado={historia.asignado.slice(0, 12)}
            banda={banda}
            historia={historiaGasto(gasto36)}
            seleccionado={movsAbierto ? mesMovs : null}
            onSeleccionar={abrirMovs}
          />
        )}
        {vista === 'saldo' && (
          <GraficoSaldo
            meses={mesesSaldo}
            saldo={[...historia.saldo.slice(0, 12), saldo]}
            meta={
              objetivoValido &&
              (objetivoValido.tipo === 'una-vez' ||
                objetivoValido.tipo === 'anual')
                ? {
                    importe: objetivoValido.importe,
                    mes: (resumen?.fecha ?? objetivoValido.fecha).slice(0, 7),
                  }
                : null
            }
            onSeleccionar={abrirMovs}
          />
        )}
        {vista === 'meses' && (
          <MapaMeses
            filas={filasMapa}
            valores={valoresMapa}
            onSeleccionar={abrirMovs}
          />
        )}
        <MovimientosMes
          categoryId={id}
          mes={mesMovs}
          abierto={movsAbierto}
          onAlternar={() => setMovsAbierto(a => !a)}
        />
      </Seccion>

      {!category.is_income && (
        <AsesorFicha
          categoryId={id}
          month={month}
          meses={meses36}
          gasto={gasto36}
          asignado={asignado}
          falta={faltaMes}
          tieneObjetivo={objetivoValido != null}
          resumen={resumen}
          onDormir={meses =>
            sueno.dormirHasta(
              monthUtils.addMonths(month, meses),
              asignado,
              saldo,
            )
          }
          mesesHistoria={Math.max(mesesConHistoria(gasto36), 12)}
        />
      )}

      {!category.is_income && <CuentaFicha categoryId={id} month={month} />}

      {!category.is_income && pagos.fijos.has(id) && (
        <GaviFicha categoriaId={id} nombre={category.name} month={month} />
      )}

      {!category.is_income && (
        <PagoFicha
          categoryId={id}
          nombre={category.name}
          month={month}
          objetivo={objetivo}
          pagada={pagada}
          fijo={pagos.fijos.has(id)}
          manual={pagos.manuales.includes(id)}
          setFijo={fijo => pagos.setFijo(id, fijo)}
          marcar={p => pagos.marcar(id, p)}
        />
      )}

      <NotasFicha category={category} nota={nota} />

      <PieFicha category={category} />

      <Text
        style={{
          fontSize: 12,
          color: color.fg3,
          textAlign: 'center',
        }}
      >
        {t('Viewing {{month}}', {
          month: monthUtils.format(month, 'MMMM yyyy', locale),
        })}
      </Text>
      {ayudaAbierta && (
        <AyudaGavi
          pantalla={category.name}
          preguntas={preguntasAyuda}
          onCerrar={onCerrarAyuda}
        />
      )}
    </View>
  );
}

const ESTADO_CABECERA: Record<
  ReturnType<typeof estadoFila>['tipo'],
  EstadoPildora
> = {
  'gastado-de-mas': 'rojo',
  falta: 'aviso',
  sobrefinanciada: 'ok',
  financiada: 'ok',
  'sin-objetivo': 'neutro',
  ignorada: 'ignorada',
};

function Cabecera({
  nombreMes,
  nombreMesCorto,
  saldo,
  asignado,
  actividad,
  estado,
  progreso,
  meta,
  resumen,
  onActividad,
  pagada,
  falta,
  onAsignar,
  onMover,
  colorAnillo,
}: {
  pagada: boolean;
  /** H44: lo que falta este mes; > 0 cambia la píldora por «Asignar X». */
  falta: IntegerAmount;
  onAsignar: () => void;
  onMover: () => void;
  colorAnillo: string;
  resumen: ReturnType<typeof resumenObjetivo> | null;
  nombreMes: string;
  nombreMesCorto: string;
  saldo: IntegerAmount;
  asignado: IntegerAmount;
  actividad: IntegerAmount;
  estado: ReturnType<typeof estadoFila>;
  progreso: number | null;
  meta: IntegerAmount | null;
  onActividad: () => void;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const fmt = (v: number) => format(v, 'financial');
  const textoEstado = (() => {
    switch (estado.tipo) {
      case 'gastado-de-mas':
        return t('Overspent by {{amount}}', { amount: fmt(estado.importe) });
      case 'falta':
        return t('{{amount}} short this month', {
          amount: fmt(estado.importe),
        });
      case 'sobrefinanciada':
      case 'financiada':
        // Objetivo por fecha: dice también lo que sobra o lo adelantado.
        if (resumen && resumen.sobrante > 0) {
          return t('Covered · {{amount}} over the goal', {
            amount: fmt(resumen.sobrante),
          });
        }
        return t('Covered this month');
      case 'ignorada':
        return t('Enough this month');
      default:
        return saldo > 0 ? t('Money available') : t('Nothing pending');
    }
  })();
  const anterior = saldo - asignado - actividad;

  return (
    <View style={{ ...estiloTarjeta('normal', 0), overflow: 'hidden' }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          padding: 16,
          gap: 12,
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 6 }}>
          <Text style={{ ...texto.etiqueta, color: color.fg3 }}>
            {t('Available in {{month}}', { month: nombreMes })}
          </Text>
          <PrivacyFilter>
            <Text
              data-testid="ficha-disponible"
              style={{
                ...texto.heroe,
                ...num,
                color: saldo < 0 ? color.bad : color.fg,
              }}
            >
              {fmt(saldo)}
            </Text>
          </PrivacyFilter>
          {falta > 0 ? (
            <View
              style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}
              data-testid="ficha-falta"
            >
              <Boton
                onPress={onAsignar}
                data-testid="ficha-asignar-falta"
                style={{ padding: '0 16px', fontSize: 14 }}
              >
                {t('Assign {{amount}} in {{month}}', {
                  amount: fmt(falta),
                  month: nombreMes,
                })}
              </Boton>
              <Boton
                variante="fantasma"
                onPress={onMover}
                data-testid="ficha-mover-falta"
                style={{ padding: '0 14px', fontSize: 14 }}
              >
                <Trans>Move…</Trans>
              </Boton>
            </View>
          ) : (
            <View style={{ flexDirection: 'row' }}>
              <Pildora
                estado={ESTADO_CABECERA[estado.tipo]}
                data-testid="ficha-estado"
              >
                {estado.tipo === 'financiada' ||
                estado.tipo === 'sobrefinanciada' ? (
                  <Icono nombre="check" size={13} strokeWidth={3} />
                ) : null}
                {textoEstado}
              </Pildora>
            </View>
          )}
          {pagada && (
            <View style={{ flexDirection: 'row' }}>
              <Pildora estado="neutro" data-testid="ficha-pagada">
                <CheckPagada size={13} />
                <Trans>Paid this month</Trans>
              </Pildora>
            </View>
          )}
        </View>
        {progreso != null && meta != null && (
          <Anillo progreso={progreso} meta={meta} trazo={colorAnillo} />
        )}
      </View>
      <View
        style={{
          flexDirection: 'row',
          borderTop: `1px solid ${color.line}`,
        }}
        data-ayuda="ficha-desglose"
      >
        <Desglose
          primera
          etiqueta={t('From last month')}
          valor={fmt(anterior)}
          tono={anterior < 0 ? color.bad : color.fg}
        />
        <Desglose
          etiqueta={t('Assigned {{month}}', { month: nombreMesCorto })}
          valor={`${asignado > 0 ? '+' : ''}${fmt(asignado)}`}
          tono={asignado > 0 ? color.ok : color.fg}
        />
        <Desglose
          etiqueta={t('Activity {{month}}', { month: nombreMesCorto })}
          valor={fmt(actividad)}
          tono={actividad < 0 ? color.bad : color.fg}
          onPress={onActividad}
        />
      </View>
    </View>
  );
}

function Desglose({
  primera = false,
  etiqueta,
  valor,
  tono,
  onPress,
}: {
  primera?: boolean;
  etiqueta: string;
  valor: string;
  tono: string;
  onPress?: () => void;
}) {
  const contenido = (
    <>
      <Text
        style={{
          ...densidad.pequeno,
          fontWeight: 500,
          color: color.fg3,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {etiqueta}
      </Text>
      <PrivacyFilter>
        <Text style={{ ...densidad.cifra, color: tono }}>{valor}</Text>
      </PrivacyFilter>
    </>
  );
  const estilo = {
    flex: 1,
    minWidth: 0,
    padding: '10px 12px',
    gap: 2,
    alignItems: 'flex-start' as const,
    borderLeft: primera ? undefined : `1px solid ${color.line}`,
  };
  return onPress ? (
    <Button
      variant="bare"
      onPress={onPress}
      style={{ ...estilo, borderRadius: 0, justifyContent: 'center' }}
    >
      <View style={{ gap: 2, minWidth: 0, width: '100%' }}>{contenido}</View>
    </Button>
  ) : (
    <View style={estilo}>{contenido}</View>
  );
}

function Anillo({
  progreso,
  meta,
  trazo,
}: {
  progreso: number;
  meta: IntegerAmount;
  /** H46: color del estado (atrasado = aviso…), no siempre el acento. */
  trazo: string;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const r = 34;
  const c = 2 * Math.PI * r;
  return (
    <View
      style={{ position: 'relative', width: 84, height: 84, flexShrink: 0 }}
      data-testid="ficha-anillo"
    >
      <svg width={84} height={84} viewBox="0 0 84 84" aria-hidden>
        <circle
          cx={42}
          cy={42}
          r={r}
          fill="none"
          stroke={color.surface3}
          strokeWidth={8}
        />
        <circle
          cx={42}
          cy={42}
          r={r}
          fill="none"
          stroke={trazo}
          strokeWidth={8}
          strokeLinecap="round"
          strokeDasharray={`${c * progreso} ${c}`}
          transform="rotate(-90 42 42)"
        />
      </svg>
      <View
        style={{
          position: 'absolute',
          inset: 0,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ fontSize: 17, fontWeight: 800, ...num }}>
          {Math.round(progreso * 100)} %
        </Text>
        <PrivacyFilter>
          <Text style={{ fontSize: 11, fontWeight: 600, color: color.fg3 }}>
            {t('of {{amount}}', {
              amount: format(meta, 'financial-no-decimals'),
            })}
          </Text>
        </PrivacyFilter>
      </View>
    </View>
  );
}

function Seccion({
  icono,
  titulo,
  derecha,
  children,
  testId,
}: {
  icono: NombreIcono;
  titulo: string;
  derecha?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <View
      style={{ ...estiloTarjeta('normal', 16), gap: 10, minWidth: 0 }}
      data-testid={testId}
    >
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 10,
          minHeight: TACTIL,
          margin: '-6px 0',
        }}
      >
        <IconoCaja icono={icono} tono="acento" size={32} />
        <Text style={{ ...texto.titulo, flex: 1, minWidth: 0 }}>{titulo}</Text>
        {derecha}
      </View>
      {children}
    </View>
  );
}

const ESTADO_OBJETIVO: Record<EstadoObjetivo, EstadoPildora> = {
  rumbo: 'ok',
  cumplido: 'ok',
  atrasado: 'aviso',
};

function TarjetaObjetivo({
  category,
  month,
  objetivo,
  estado,
  resumen,
  saldo,
  asignado,
  actividad,
  sueno,
  ofreceSuficiente,
  pop,
  editarAlAbrir,
  sugerencias,
  onAsignar,
  travesia,
  envoltorio,
}: {
  /** Tipo y travesía de la categoría (`useTravesiaCategoria`). */
  travesia: TravesiaCategoria;
  /**
   * Travesía: la meta va dentro de la tarjeta de la botella (una sola
   * tarjeta «Travesía»). Recibe lo de la derecha del título y el cuerpo.
   */
  envoltorio?: (partes: { derecha: ReactNode; cuerpo: ReactNode }) => ReactNode;
  sugerencias: ReturnType<typeof sugerenciasImporte> | null;
  onAsignar: (importe: IntegerAmount, asignadoAntes: IntegerAmount) => void;
  category: CategoryEntity;
  month: string;
  objetivo: Objetivo | null | 'otro';
  estado: EstadoObjetivo | null;
  resumen: ReturnType<typeof resumenObjetivo> | null;
  saldo: IntegerAmount;
  asignado: IntegerAmount;
  actividad: IntegerAmount;
  sueno: ReturnType<typeof useIgnorarMes>;
  /** Si se enseña el botón ✓ «Suficiente este mes» (`opcionSuficiente`). */
  ofreceSuficiente: 'poner' | 'quitar' | null;
  /** Marca de tiempo del último despertar automático (0 = ninguno). */
  pop: number;
  editarAlAbrir: boolean;
}) {
  const { ignorada, dormida, dormirHasta } = sueno;
  const locale = useLocale();
  const { alternar: alternarPausa } = usePausarMes(
    category.id,
    category.name,
    month,
  );
  const { t } = useTranslation();
  const format = useFormat();
  const resumenTexto = useResumenObjetivo();
  const applyBudgetAction = useBudgetActions();
  const { showUndoNotification } = useUndo();
  // Si se cerró, al volver con «atrás» sigue cerrado aunque la URL diga
  // «editar» (antes se reabría solo).
  const [abierto, setAbierto] = useEstadoDeVista(
    'editor-objetivo',
    editarAlAbrir,
  );
  const [anim, setAnim] = useState<{
    modo: 'poner' | 'quitar';
    clave: number;
  } | null>(null);
  const finAnim = useCallback(() => setAnim(null), []);
  useEffect(() => {
    if (pop) {
      setAnim({ modo: 'quitar', clave: pop });
    }
  }, [pop]);
  const fmt = (v: number) => format(v, 'financial');

  const mesCorto = (mes: string) =>
    monthUtils.format(mes, 'MMM yyyy', locale).replace('.', '');
  const textoEstado = dormida
    ? t('Enough until {{month}}', { month: mesCorto(dormida.hasta) })
    : ignorada
      ? t('Enough this month')
      : estado === 'rumbo'
        ? `⛵ ${t('Steady course')}`
        : estado === 'cumplido'
          ? t('Reached')
          : // Una travesía nunca «va con retraso»: el ritmo ya lo cuenta la
            // botella, solo informativo.
            estado === 'atrasado' && !envoltorio
            ? t('Behind')
            : null;

  // «Devolver X a Listo para asignar» (solo el sobrante sobre la meta, que se
  // puede mover sin riesgo). Si cabe en lo asignado este mes, se baja lo
  // asignado (`budget-amount`, nunca por debajo de 0); si no, el traspaso de
  // Actual de la categoría a «Listo para asignar» (`transfer-category` con
  // `to-budget`, el mismo que usa «Mover» del menú del saldo). Con deshacer.
  const devolver = () => {
    if (!resumen || resumen.sobrante <= 0) {
      return;
    }
    const importe = resumen.sobrante;
    if (importe <= asignado) {
      applyBudgetAction.mutate({
        month,
        type: 'budget-amount',
        args: { category: category.id, amount: asignado - importe },
      } as ApplyBudgetActionPayload);
    } else {
      applyBudgetAction.mutate({
        month,
        type: 'transfer-category',
        args: {
          amount: importe,
          from: category.id,
          to: 'to-budget',
          currencyCode: format.currency.code,
        },
      } as ApplyBudgetActionPayload);
    }
    showUndoNotification({
      message: t(
        '{{amount}} returned to Ready to Assign from {{categoryName}}.',
        {
          amount: fmt(importe),
          categoryName: category.name,
        },
      ),
    });
  };

  // Opciones de «dormir»: este mes, hasta el cobro (objetivo con fecha) y
  // hasta <mes> cuando el Asesor ve N meses de adelanto.
  const { mesCobro, mesAdelanto } = opcionesSueno(resumen, month);
  // B4 · H32: un toque marca (o quita) «Suficiente» este mes, con aviso y
  // «Deshacer»; «Suficiente hasta…» se ofrece debajo mientras está marcada.
  const dormirMas = (hasta: string) => {
    dormirHasta(hasta, asignado, saldo, actividad);
    setAnim({ modo: 'poner', clave: Date.now() });
  };
  const alternarIgnorar = () => {
    setAnim({
      modo: ignorada ? 'quitar' : 'poner',
      clave: Date.now(),
    });
    alternarPausa();
  };
  const verMasLargas = ignorada && !dormida && (mesCobro || mesAdelanto);

  const derecha = (
    <>
      {objetivo !== null && (
        <View style={{ position: 'relative' }}>
          {ignorada && !anim && ofreceSuficiente !== null && (
            <MarcaSuficiente data-testid="suficiente-marca" />
          )}
          {ofreceSuficiente !== null && (
            <Button
              variant="bare"
              onPress={alternarIgnorar}
              aria-pressed={ignorada}
              aria-label={
                ignorada ? t('Remove "enough"') : t('Enough this month')
              }
              data-testid="ficha-ignorar"
              style={{
                position: 'relative',
                zIndex: 1,
                width: TACTIL,
                height: TACTIL,
                minHeight: TACTIL,
                padding: 0,
                borderRadius: radio.pildora,
                backgroundColor: 'transparent',
              }}
            >
              <Icono
                nombre="check"
                size={18}
                strokeWidth={2.6}
                style={{
                  color: ignorada ? color.accent : color.fg3,
                  // Mientras se dibuja el ✓ de la animación, el del botón
                  // espera oculto para no verse doble.
                  opacity: anim?.modo === 'poner' ? 0 : ignorada ? 1 : 0.5,
                  // Sin transición de opacidad: al acabar, el ✓ dibujado
                  // y el del botón se relevan sin parpadeo.
                  transition: 'color 180ms',
                }}
              />
            </Button>
          )}
          {anim && (
            <AnimacionSuficiente
              key={anim.clave}
              modo={anim.modo}
              onFin={finAnim}
            />
          )}
        </View>
      )}
      {textoEstado && objetivo !== null && (
        <Pildora
          estado={
            dormida
              ? 'neutro'
              : ignorada
                ? 'ignorada'
                : ESTADO_OBJETIVO[estado ?? 'rumbo']
          }
          data-testid="objetivo-estado"
        >
          {textoEstado}
        </Pildora>
      )}
    </>
  );
  const cuerpo = (
    <>
      {verMasLargas && (
        <View
          data-testid="ficha-pausa-larga"
          style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}
        >
          {mesCobro && (
            <Boton
              variante="fantasma"
              onPress={() => dormirMas(mesCobro)}
              data-testid="ficha-pausa-cobro"
            >
              {t('Enough until the charge ({{month}})', {
                month: mesCorto(mesCobro),
              })}
            </Boton>
          )}
          {mesAdelanto && (
            <Boton
              variante="fantasma"
              onPress={() => dormirMas(mesAdelanto)}
              data-testid="ficha-pausa-adelanto"
            >
              {t('Enough until {{month}}', {
                month: mesCorto(mesAdelanto),
              })}
            </Boton>
          )}
        </View>
      )}

      {!abierto && objetivo === null && (
        <Boton
          variante="fantasma"
          onPress={() => setAbierto(true)}
          data-testid="objetivo-anadir"
        >
          <Trans>Add target</Trans>
        </Boton>
      )}

      {!abierto && objetivo !== null && (
        <Button
          variant="bare"
          onPress={() => setAbierto(true)}
          data-testid="objetivo-resumen"
          aria-label={t('Edit target')}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 8,
            padding: 0,
            borderRadius: 0,
            backgroundColor: 'transparent',
            textAlign: 'left',
            // En la tarjeta «Travesía», la meta va bajo la botella.
            ...(envoltorio && {
              marginTop: 4,
              paddingTop: 10,
              borderTop: `1px dashed ${color.line2}`,
            }),
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <PrivacyFilter>
              <Text style={{ fontSize: 15, fontWeight: 800, color: color.fg }}>
                {resumenTexto(objetivo, month)}
              </Text>
            </PrivacyFilter>
            {objetivo !== 'otro' && objetivo.tipo === 'una-vez' && (
              <Text style={{ fontSize: 13, color: color.fg3 }}>
                {t('once')}
              </Text>
            )}
          </View>
          {resumen && (
            <>
              {/* En una travesía, lo que llevas ya lo dice la botella. */}
              {!envoltorio && (
                <BarraProgreso
                  valor={resumen.progreso}
                  color={colorProgreso(resumen.estado, ignorada)}
                />
              )}
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
              >
                {!envoltorio && (
                  <PrivacyFilter>
                    <Text style={{ fontSize: 13, color: color.fg2 }}>
                      {resumen.falta > 0
                        ? t('Missing {{amount}}', {
                            amount: fmt(resumen.falta),
                          })
                        : resumen.sobrante > 0
                          ? t('{{amount}} over the goal', {
                              amount: fmt(resumen.sobrante),
                            })
                          : t('Nothing missing')}
                    </Text>
                  </PrivacyFilter>
                )}
                <PrivacyFilter>
                  <Text style={{ fontSize: 13, color: color.fg2, ...num }}>
                    {(resumen.cuota === 0 || resumen.falta === 0) &&
                    resumen.cuotaNormal
                      ? t('Usually about {{amount}}/month', {
                          amount: fmt(resumen.cuotaNormal),
                        })
                      : t('About {{amount}}/month', {
                          amount: fmt(resumen.cuota),
                        })}
                  </Text>
                </PrivacyFilter>
              </View>
              {resumen.adelanto > 0 &&
                resumen.falta > 0 &&
                resumen.estado === 'rumbo' &&
                !ignorada && (
                  <PrivacyFilter>
                    <Text
                      style={{ ...texto.secundario, color: color.fg2 }}
                      data-testid="objetivo-adelanto"
                    >
                      {t(
                        "You're ahead of schedule: that's why the payment drops to {{amount}}/month.",
                        { amount: fmt(resumen.cuota) },
                      )}
                    </Text>
                  </PrivacyFilter>
                )}
            </>
          )}
        </Button>
      )}

      {!abierto && resumen && resumen.sobrante > 0 && (
        <Boton
          variante="fantasma"
          onPress={devolver}
          data-testid="objetivo-devolver"
          style={{ alignSelf: 'flex-start', padding: '0 14px', fontSize: 14 }}
        >
          {t('Return {{amount}} to Ready to Assign', {
            amount: fmt(resumen.sobrante),
          })}
        </Boton>
      )}

      {abierto && (
        <EditorObjetivo
          category={category}
          month={month}
          objetivo={objetivo}
          saldo={saldo}
          asignado={asignado}
          sugerencias={sugerencias}
          onAsignar={onAsignar}
          travesia={travesia}
          onCerrar={() => setAbierto(false)}
        />
      )}
    </>
  );

  if (envoltorio) {
    return envoltorio({ derecha, cuerpo });
  }

  return (
    <View
      style={{ ...estiloTarjeta('normal', 16), gap: 10 }}
      data-testid="ficha-objetivo"
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          minHeight: TACTIL,
          margin: '-6px 0',
        }}
      >
        <IconoCaja icono="target" tono="ok" size={32} />
        <Text style={{ ...texto.titulo }}>
          <Trans>Target</Trans>
        </Text>
        <View style={{ flex: 1 }} />
        {derecha}
      </View>
      {cuerpo}
    </View>
  );
}

function Campo({
  etiqueta,
  children,
  dataAyuda,
}: {
  etiqueta: string;
  children: ReactNode;
  /** Lo que señala la ayuda de Gavi. */
  dataAyuda?: string;
}) {
  return (
    <View style={{ flex: 1, minWidth: 0, gap: 6 }} data-ayuda={dataAyuda}>
      <Text style={{ ...texto.etiqueta, color: color.fg3 }}>{etiqueta}</Text>
      {children}
    </View>
  );
}

const estiloControl = {
  minHeight: TACTIL + 4,
  width: '100%',
  minWidth: 0,
  marginLeft: 0,
  marginRight: 0,
  boxSizing: 'border-box',
  fontSize: 16,
  borderRadius: radio.boton,
} as const;

function EditorObjetivo({
  category,
  month,
  objetivo,
  saldo,
  asignado,
  sugerencias,
  onAsignar,
  travesia,
  onCerrar,
}: {
  category: CategoryEntity;
  month: string;
  objetivo: Objetivo | null | 'otro';
  saldo: IntegerAmount;
  asignado: IntegerAmount;
  sugerencias: ReturnType<typeof sugerenciasImporte> | null;
  onAsignar: (importe: IntegerAmount, asignadoAntes: IntegerAmount) => void;
  travesia: TravesiaCategoria;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const format = useFormat();
  const { guardar, guardando } = useGuardarObjetivo(category.id, month);
  const [formulario, setFormulario] = useState<Formulario>(() =>
    formularioDesde(
      objetivo === 'otro' ? null : objetivo,
      month,
      format.forEdit,
    ),
  );
  const cambiar = (cambios: Partial<Formulario>) =>
    setFormulario(prev => ({ ...prev, ...cambios }));

  const nuevo = objetivoDesde(formulario);
  const conFecha = formulario.tipo === 'anual' || formulario.tipo === 'una-vez';
  const fechaPasada =
    nuevo != null &&
    (nuevo.tipo === 'anual' || nuevo.tipo === 'una-vez') &&
    nuevo.fecha.slice(0, 7) < month;
  const puedeGuardar = nuevo != null && !fechaPasada && !guardando;

  const onGuardar = async () => {
    if (!nuevo || !puedeGuardar) {
      return;
    }
    const lineas = lineasDeObjetivo(nuevo, monthUtils.currentDay());
    const ok = await guardar(
      lineas,
      t('Target saved for {{categoryName}}.', { categoryName: category.name }),
      botonAsignar,
    );
    if (ok) {
      onCerrar();
      // Una meta con fecha nueva es una travesía: se zarpa con su carta.
      if (
        lineasConFecha(lineas) &&
        !category.is_income &&
        !travesia.esFijo &&
        travesia.tipo !== 'travesia'
      ) {
        dispatch(
          pushModal({
            modal: {
              name: 'zarpar',
              options: { categoryId: category.id, month, zarpe: true },
            },
          }),
        );
      }
    }
  };
  // H49: con el objetivo ya guardado y recalculado, lo que falta este mes
  // (leído de la hoja, no de la cuenta local) → «Asignar X ahora».
  const botonAsignar = async () => {
    if (month < monthUtils.currentMonth() || category.is_income) {
      return null;
    }
    const hoja = monthUtils.sheetForMonth(month);
    const leer = async (name: string) => {
      const celda = await send('get-cell', { sheetName: hoja, name });
      return typeof celda?.value === 'number' ? celda.value : null;
    };
    const [goalNuevo, largo, asignadoAhora, saldoAhora] = await Promise.all([
      leer(envelopeBudget.catGoal(category.id)),
      leer(envelopeBudget.catLongGoal(category.id)),
      leer(envelopeBudget.catBudgeted(category.id)),
      leer(envelopeBudget.catBalance(category.id)),
    ]);
    const falta = faltante({
      goal: goalNuevo,
      longGoal: largo === 1,
      budgeted: asignadoAhora ?? 0,
      balance: saldoAhora ?? 0,
      spent: 0,
    });
    if (falta <= 0) {
      return null;
    }
    return {
      title: t('Assign {{amount}} now', { amount: format(falta, 'financial') }),
      action: () => onAsignar(falta, asignadoAhora ?? 0),
    };
  };
  const onQuitar = async () => {
    const ok = await guardar(
      [],
      t('Target removed from {{categoryName}}.', {
        categoryName: category.name,
      }),
    );
    if (ok) {
      onCerrar();
    }
  };

  const importe = currencyToInteger(formulario.importeTexto) ?? 0;
  const cuota =
    nuevo && (nuevo.tipo === 'anual' || nuevo.tipo === 'una-vez')
      ? cuotaMensual(nuevo.importe, saldo - asignado, nuevo.fecha, month)
      : null;
  const mesesHasta =
    nuevo && (nuevo.tipo === 'anual' || nuevo.tipo === 'una-vez')
      ? monthUtils.differenceInCalendarMonths(nuevo.fecha.slice(0, 7), month) +
        1
      : 0;

  return (
    <View
      style={{
        gap: 12,
        paddingTop: 10,
        borderTop: `1px dashed ${color.line2}`,
      }}
      data-testid="objetivo-editor"
    >
      {objetivo === 'otro' && (
        <Text
          style={{
            padding: '8px 12px',
            borderRadius: radio.boton,
            backgroundColor: color.warnSoft,
            color: color.warn,
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {t(
            'This category has an automation this screen cannot edit. Saving here replaces it.',
          )}
        </Text>
      )}
      <Pestanas tipo={formulario.tipo} onChange={tipo => cambiar({ tipo })} />
      <View
        style={{ flexDirection: 'row', gap: 10 }}
        data-ayuda="objetivo-cuanto"
      >
        <Campo etiqueta={t('I need')}>
          <CampoImporte
            aria-label={t('I need')}
            valor={formulario.importeTexto.trim() ? importe : null}
            onCambiar={centimos =>
              cambiar({
                importeTexto: centimos == null ? '' : format.forEdit(centimos),
              })
            }
            data-testid="objetivo-importe"
            style={estiloControl}
            estiloTexto={{ fontSize: 16, fontWeight: 600 }}
          />
        </Campo>
        {conFecha ? (
          <Campo
            etiqueta={
              formulario.tipo === 'anual' ? t('Needed by') : t('Needed for')
            }
          >
            <InputField
              aria-label={t('Date')}
              type="date"
              value={formulario.fecha}
              min={`${month}-01`}
              onChangeValue={fecha => cambiar({ fecha })}
              data-testid="objetivo-fecha"
              style={estiloControl}
            />
          </Campo>
        ) : formulario.tipo === 'mensual' ? (
          <Campo etiqueta={t('Needed by')}>
            <Select
              value={formulario.dia == null ? 0 : formulario.dia}
              onChange={dia => cambiar({ dia: dia === 0 ? null : dia })}
              options={[
                [0, t('End of month')],
                ...Array.from(
                  { length: 31 },
                  (_, i) =>
                    [i + 1, t('Day {{day}}', { day: i + 1 })] as [
                      number,
                      string,
                    ],
                ),
              ]}
              style={{ ...estiloControl, justifyContent: 'space-between' }}
            />
          </Campo>
        ) : null}
      </View>
      {(formulario.tipo === 'mensual' || formulario.tipo === 'semanal') && (
        <Campo
          dataAyuda="objetivo-modo"
          etiqueta={
            formulario.tipo === 'mensual'
              ? t('Next month I want to')
              : t('Next week I want to')
          }
        >
          <Select
            value={formulario.modo}
            onChange={modo => cambiar({ modo })}
            options={[
              [
                'apartar',
                t('Set aside another {{amount}}', {
                  amount: format(importe, 'financial'),
                }),
              ],
              [
                'rellenar',
                t('Refill up to {{amount}}', {
                  amount: format(importe, 'financial'),
                }),
              ],
            ]}
            style={{ ...estiloControl, justifyContent: 'space-between' }}
          />
        </Campo>
      )}
      {formulario.tipo === 'anual' && (
        <Campo etiqueta={t('Repeat')}>
          <Select
            value={formulario.cadaMeses}
            onChange={cadaMeses => cambiar({ cadaMeses })}
            options={CADENCIAS_ANUALES.map(
              meses =>
                [
                  meses,
                  meses === 12
                    ? t('Every year')
                    : meses === 24
                      ? t('Every 2 years')
                      : t('Every {{count}} months', { count: meses }),
                ] as [CadenciaMeses, string],
            )}
            style={{ ...estiloControl, justifyContent: 'space-between' }}
          />
        </Campo>
      )}
      {sugerencias && (sugerencias.media > 0 || sugerencias.mesPasado > 0) && (
        <View
          style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}
          data-testid="objetivo-sugerencias"
        >
          {sugerencias.media > 0 && (
            <Boton
              variante="fantasma"
              onPress={() =>
                cambiar({ importeTexto: format.forEdit(sugerencias.media) })
              }
              data-testid="objetivo-usar-media"
              style={{ padding: '0 12px', fontSize: 13 }}
            >
              <PrivacyFilter>
                {t('Monthly average {{amount}} → Use', {
                  amount: format(sugerencias.media, 'financial'),
                })}
              </PrivacyFilter>
            </Boton>
          )}
          {sugerencias.mesPasado > 0 && (
            <Boton
              variante="fantasma"
              onPress={() =>
                cambiar({
                  importeTexto: format.forEdit(sugerencias.mesPasado),
                })
              }
              data-testid="objetivo-usar-mes-pasado"
              style={{ padding: '0 12px', fontSize: 13 }}
            >
              <PrivacyFilter>
                {t('Last month {{amount}} → Use', {
                  amount: format(sugerencias.mesPasado, 'financial'),
                })}
              </PrivacyFilter>
            </Boton>
          )}
        </View>
      )}
      {fechaPasada && (
        <Text style={{ fontSize: 13, color: color.bad }}>
          <Trans>The date must be in this month or later.</Trans>
        </Text>
      )}
      {cuota != null && !fechaPasada && (
        <PrivacyFilter>
          <Text
            style={{ ...texto.secundario, color: color.fg2 }}
            data-testid="objetivo-cuota"
          >
            {t(
              'Works out at {{amount}}/month for {{count}} months. This month you already assigned {{assigned}}.',
              {
                amount: format(cuota, 'financial'),
                count: mesesHasta,
                assigned: format(asignado, 'financial'),
              },
            )}
          </Text>
        </PrivacyFilter>
      )}
      <View
        style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
        data-ayuda="objetivo-botones"
      >
        {objetivo !== null && (
          <Button
            variant="bare"
            onPress={() => void onQuitar()}
            isDisabled={guardando}
            data-testid="objetivo-eliminar"
            style={{
              minHeight: TACTIL,
              padding: '0 4px',
              color: color.bad,
              fontWeight: 700,
              fontSize: 14,
              backgroundColor: 'transparent',
            }}
          >
            <Trans>Remove target</Trans>
          </Button>
        )}
        <View style={{ flex: 1 }} />
        <Button
          variant="bare"
          onPress={onCerrar}
          data-testid="objetivo-cancelar"
          style={{
            minHeight: TACTIL,
            padding: '0 10px',
            color: color.fg2,
            fontWeight: 700,
            fontSize: 15,
            backgroundColor: 'transparent',
          }}
        >
          <Trans>Cancel</Trans>
        </Button>
        <Boton
          onPress={() => void onGuardar()}
          isDisabled={!puedeGuardar}
          data-testid="objetivo-guardar"
          style={{ padding: '0 20px', opacity: puedeGuardar ? 1 : 0.5 }}
        >
          <Trans>Save</Trans>
        </Boton>
      </View>
    </View>
  );
}

function NotasFicha({
  category,
  nota,
}: {
  category: CategoryEntity;
  nota: string | null | undefined;
}) {
  const { t } = useTranslation();
  const visibles = notaSinObjetivo(nota);
  const [editando, setEditando] = useState(false);
  return (
    <Seccion
      icono="note"
      titulo={t('Notes')}
      testId="ficha-notas"
      derecha={
        <Button
          variant="bare"
          onPress={() => setEditando(true)}
          data-testid="ficha-notas-editar"
          style={{
            minHeight: TACTIL,
            padding: '0 4px',
            color: color.fg3,
            fontSize: 14,
            fontWeight: 600,
            backgroundColor: 'transparent',
          }}
        >
          <Trans>Edit</Trans>
        </Button>
      }
    >
      {visibles.length > 0 ? (
        <Notes
          notes={visibles}
          editable={false}
          focused={false}
          getStyle={() => ({ padding: 0, fontSize: 14 })}
        />
      ) : (
        <Text style={{ ...texto.secundario, color: color.fg3 }}>
          <Trans>No notes</Trans>
        </Text>
      )}
      {editando && (
        <HojaNotas
          category={category}
          nota={nota}
          onCerrar={() => setEditando(false)}
        />
      )}
    </Seccion>
  );
}

/**
 * B4 · H48: hoja de notas de la ficha. Se edita solo el texto de la persona;
 * las líneas del objetivo (`#template`, `#goal`…) no se ven y se vuelven a
 * poner al guardar (`separarObjetivoDeNota` / `unirNotaYObjetivo`).
 */
function HojaNotas({
  category,
  nota,
  onCerrar,
}: {
  category: CategoryEntity;
  nota: string | null | undefined;
  onCerrar: () => void;
}) {
  const { t } = useTranslation();
  const [escrito, setEscrito] = useState(
    () => separarObjetivoDeNota(nota).texto,
  );
  const [guardando, setGuardando] = useState(false);
  const guardar = async () => {
    if (guardando) {
      return;
    }
    setGuardando(true);
    try {
      // Las líneas del objetivo, de la nota vigente al guardar.
      const { lineas } = separarObjetivoDeNota(nota);
      const nueva = unirNotaYObjetivo(escrito, lineas);
      if (nueva !== (nota ?? '')) {
        await send('notes-save', { id: category.id, note: nueva });
      }
      onCerrar();
    } finally {
      setGuardando(false);
    }
  };
  return (
    <Hoja aria-label={t('Notes')} data-testid="hoja-notas" onCerrar={onCerrar}>
      <View style={{ gap: 12 }}>
        <View>
          <Text style={{ ...texto.titulo, color: color.fg }}>
            <Trans>Notes</Trans>
          </Text>
          <Text style={{ ...texto.secundario, color: color.fg2 }}>
            {category.name}
          </Text>
        </View>
        <textarea
          aria-label={t('Notes')}
          data-testid="hoja-notas-texto"
          value={escrito}
          placeholder={t('Write anything you want to remember')}
          onChange={e => setEscrito(e.target.value)}
          style={{
            height: '32vh',
            minHeight: 140,
            resize: 'none',
            boxSizing: 'border-box',
            width: '100%',
            borderRadius: radio.boton,
            border: `1.5px solid ${color.line2}`,
            padding: 14,
            fontSize: 16,
            fontFamily: 'inherit',
            lineHeight: 1.4,
            backgroundColor: color.surface,
            color: color.fg,
          }}
        />
        <Boton
          onPress={() => void guardar()}
          isDisabled={guardando}
          data-testid="hoja-notas-guardar"
          style={{ width: '100%' }}
        >
          <Trans>Save</Trans>
        </Boton>
      </View>
    </Hoja>
  );
}

function PieFicha({ category }: { category: CategoryEntity }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const saveCategory = useSaveCategoryMutation();
  const deleteCategory = useDeleteCategoryMutation();
  const { showUndoNotification } = useUndo();
  const [renombrando, setRenombrando] = useState(false);
  // Borrar no tiene «Deshacer»: siempre se pregunta antes. Si tiene
  // movimientos o dinero, pregunta la hoja de traspaso de siempre; si no,
  // esta.
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);
  const partes = separarEmoji(category.name);
  const [nombre, setNombre] = useState(partes.resto);
  const [emoji, setEmoji] = useState(partes.emoji);

  const guardarNombre = () => {
    const limpio = unirEmoji(emoji, nombre);
    if (nombre.trim() && limpio !== category.name) {
      saveCategory.mutate({ category: { ...category, name: limpio } });
    }
    setRenombrando(false);
  };

  const cancelarNombre = () => {
    setNombre(partes.resto);
    setEmoji(partes.emoji);
    setRenombrando(false);
  };

  const borrar = () => {
    deleteCategory.mutate({ id: category.id });
    void navigate(-1);
  };

  const pedirBorrar = async () => {
    const conTraspaso = await send('must-category-transfer', {
      id: category.id,
    });
    if (conTraspaso) {
      borrar();
    } else {
      setConfirmarBorrar(true);
    }
  };

  const alternarOculta = () => {
    const ocultar = !category.hidden;
    saveCategory.mutate({ category: { ...category, hidden: ocultar } });
    if (ocultar) {
      showUndoNotification({
        message: t('{{category}} hidden', { category: category.name }),
        button: {
          title: t('Undo'),
          action: () =>
            saveCategory.mutate({ category: { ...category, hidden: false } }),
        },
      });
    }
  };

  if (renombrando) {
    return (
      <View style={{ ...estiloTarjeta('normal', 12), gap: 8 }}>
        <CampoNombreEmoji
          emoji={emoji}
          onEmoji={setEmoji}
          nombre={nombre}
          onNombre={setNombre}
          aria-label={t('Name')}
          onEnter={guardarNombre}
          data-testid="ficha-nombre"
        />
        <Boton
          bloque
          onPress={guardarNombre}
          isDisabled={!nombre.trim()}
          data-testid="ficha-nombre-guardar"
        >
          <Trans>Save</Trans>
        </Boton>
        <Boton
          bloque
          variante="fantasma"
          onPress={cancelarNombre}
          data-testid="ficha-nombre-cancelar"
        >
          <Trans>Cancel</Trans>
        </Boton>
      </View>
    );
  }

  const accion = (
    icono: NombreIcono,
    etiqueta: string,
    onPress: () => void,
    peligro = false,
    testId?: string,
  ) => (
    <Button
      variant="bare"
      onPress={onPress}
      data-testid={testId}
      style={{
        flex: 1,
        minHeight: TACTIL,
        gap: 6,
        fontSize: 14,
        fontWeight: 600,
        color: peligro ? color.bad : color.fg3,
        backgroundColor: 'transparent',
      }}
    >
      <Icono nombre={icono} size={16} />
      {etiqueta}
    </Button>
  );

  return (
    <View
      style={{ flexDirection: 'row', justifyContent: 'space-between' }}
      data-ayuda="ficha-pie"
    >
      {accion(
        'tag',
        t('Rename'),
        () => setRenombrando(true),
        false,
        'ficha-renombrar',
      )}
      {accion(
        'eye',
        category.hidden ? t('Show') : t('Hide'),
        alternarOculta,
        false,
        'ficha-ocultar',
      )}
      {accion(
        'trash',
        t('Delete'),
        () => void pedirBorrar(),
        true,
        'ficha-eliminar',
      )}
      {confirmarBorrar && (
        <Hoja
          aria-label={t('Delete category')}
          data-testid="hoja-borrar-categoria"
          onCerrar={() => setConfirmarBorrar(false)}
        >
          <ConfirmarHoja
            icono="trash"
            pregunta={t('Delete «{{category}}»?', { category: category.name })}
            detalle={t(
              "It has no transactions or money in any month. This can't be undone; if you only want it out of sight, hide it.",
            )}
            textoAccion={t('Delete')}
            testIdAccion="borrar-categoria-confirmar"
            onConfirmar={() => {
              setConfirmarBorrar(false);
              borrar();
            }}
            onCancelar={() => setConfirmarBorrar(false)}
          />
        </Hoja>
      )}
    </View>
  );
}

/**
 * «Pago fijo mensual» (interruptor) y «Marcar como pagada este mes» /
 * «Desmarcar». Ver `pagadas.ts`.
 */
function PagoFicha({
  categoryId,
  nombre,
  month,
  objetivo,
  pagada,
  fijo,
  manual,
  setFijo,
  marcar,
}: {
  categoryId: string;
  nombre: string;
  month: string;
  objetivo: Objetivo | null | 'otro';
  pagada: boolean;
  fijo: boolean;
  manual: boolean;
  setFijo: (fijo: boolean) => void;
  marcar: (pagada: boolean | 'no') => void;
}) {
  const { t } = useTranslation();
  return (
    <View
      style={{ ...estiloTarjeta('normal', 0), overflow: 'hidden' }}
      data-testid="ficha-pago"
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          minHeight: 52,
          padding: '0 16px',
        }}
      >
        <CheckPagada size={18} pagada={pagada} />
        <label
          htmlFor="pago-fijo"
          style={{ ...densidad.nombre, flex: 1, color: color.fg }}
        >
          <Trans>Fixed payment</Trans>
        </label>
        <Toggle
          id="pago-fijo"
          isOn={fijo}
          onToggle={setFijo}
          aria-label={t('Fixed payment')}
        />
      </View>
      {fijo && (
        <DetallePagoFijo
          categoryId={categoryId}
          nombreCategoria={nombre}
          month={month}
          objetivo={objetivo}
        />
      )}
      <Button
        variant="bare"
        // Marcada a mano: se quita la marca. Detectada: «Desmarcar este mes»
        // guarda una marca «no pagada» solo para este mes.
        onPress={() => marcar(manual ? false : pagada ? 'no' : true)}
        data-testid="ficha-marcar-pagada"
        style={{
          minHeight: TACTIL + 4,
          borderRadius: 0,
          borderTop: `1px solid ${color.line}`,
          justifyContent: 'flex-start',
          padding: '0 16px',
          fontSize: 14,
          fontWeight: 700,
          color: manual ? color.fg2 : color.accent,
          backgroundColor: 'transparent',
        }}
      >
        {manual
          ? t('Unmark')
          : pagada
            ? t('Paid this month (detected) · Unmark this month')
            : t('Mark as paid this month')}
      </Button>
    </View>
  );
}
